import { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { X, Globe } from 'lucide-react';
import { overlayDismiss } from '../../../lib/overlayDismiss';

/**
 * SmartExtractModal — 智慧擷取：從網址更新知識庫
 *
 * - 網址（一行一個）
 * - 一律整站深度爬取（crawl-site-to-kb 整站 BFS）—— 介面不再提供單頁選項
 * - 客製擷取重點（可選）：留空就抓整站
 *
 * 套用 → sendMessage 觸發 build-kb-from-files Workflow B（增量更新進既有 KB）。
 */
export function SmartExtractModal({ open, onClose, onApply, isStreaming, avatarName }) {
    const { t } = useTranslation('wsApi');
    const [urlText, setUrlText] = useState('');
    const [custom, setCustom] = useState('');

    useEffect(() => {
        if (!open) {
            setUrlText('');
            setCustom('');
        }
    }, [open]);

    if (!open) return null;

    const urls = urlText
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
    const canApply = urls.length > 0 && urls.every((u) => /^https?:\/\/.+/i.test(u));

    const handleApply = () => {
        if (isStreaming || !canApply) return;

        const urlList = urls.map((u, i) => `${i + 1}. ${u}`).join('\n');
        const customLine = custom.trim()
            ? `\n【特別著重擷取】\n${custom.trim()}\n`
            : '';

        const prompt = `請用智慧擷取功能更新這個客服 (${avatarName || 'avatar'}) 的知識庫。

【網址（深度搜尋 — 整站 BFS 爬取）】
${urlList}
${customLine}
做法：
1. 用 crawl-site-to-kb skill 對每個網址做深度爬取（BFS）。先 detect_site_type.py 判斷 render_mode — 站是 client-rendered SPA 就走 crawl_spa.py（Playwright 渲染），否則走一般 crawl.py
2. 把爬到的頁面用 build-kb-from-files skill 的 Workflow B（增量更新）append 進這個 avatar 既有的知識庫，**不要**另開新 KB node
3. vectorize 新 chunks
4. 若這個 avatar 原本沒有 KB，依新內容刷新開場白（firstMessage + 重生 greeting MP3）
5. 完成後用商家口吻摘要：爬了幾頁、加了多少內容、有沒有更新開場白（不要貼 JSON）`;

        const displayText = t('extract.displayTextDeep', { n: urls.length })
            + `\n${urls.join('\n')}`
            + (custom.trim() ? `\n${t('extract.displayFocus', { focus: custom.trim() })}` : '');

        onApply(prompt, { displayText });
        onClose();
    };

    return (
        <div
            className="fixed inset-0 bg-ink-900/30 backdrop-blur-sm z-50 grid place-items-center p-4"
            {...overlayDismiss(onClose)}
        >
            <div
                className="bg-cream-50 border border-cream-200 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-400 inline-flex items-center gap-1.5">
                            <Globe size={11} /> smart extract from web
                        </div>
                        <h2 className="heading-display text-lg font-semibold text-ink-900 mt-0.5">{t('extract.title')}</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="size-8 inline-flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-cream-100 transition"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5 flex-1 overflow-y-auto space-y-4">
                    <div className="rounded-md bg-cream-100/60 border border-cream-200 px-3 py-2 text-xs text-ink-500 leading-relaxed">
                        <Trans i18nKey="extract.intro" t={t}>
                            貼上網址，AI 會自動<strong className="text-ink-700">整站深度爬取</strong>網頁內容並更新進這個客服的知識庫。
                        </Trans>
                    </div>

                    {/* URLs */}
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2">
                            {t('extract.urlsLabel')}
                        </div>
                        <textarea
                            value={urlText}
                            onChange={(e) => setUrlText(e.target.value)}
                            rows={4}
                            placeholder={t('extract.urlsPlaceholder')}
                            className="w-full resize-none px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white font-mono leading-relaxed"
                            autoFocus
                        />
                    </div>

                    {/* Custom extraction focus */}
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 mb-2">
                            {t('extract.customLabel')}
                        </div>
                        <textarea
                            value={custom}
                            onChange={(e) => setCustom(e.target.value)}
                            rows={4}
                            placeholder={t('extract.customPlaceholder')}
                            className="w-full resize-none px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white font-sans leading-relaxed"
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-cream-200">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm rounded-md text-ink-700 hover:bg-cream-100 transition"
                    >
                        {t('common:actions.cancel')}
                    </button>
                    <button
                        onClick={handleApply}
                        disabled={isStreaming || !canApply}
                        className="px-4 py-2 text-sm rounded-md bg-ink-900 text-white hover:bg-ink-700 transition disabled:bg-cream-300 disabled:cursor-not-allowed"
                    >
                        {t('extract.apply')}
                    </button>
                </div>
            </div>
        </div>
    );
}
